/**
 * Error message mapping for user-friendly error display
 * Maps technical Solana/wallet errors to human-readable messages
 */

/**
 * Known error patterns and their user-friendly messages
 */
const ERROR_MESSAGES: Record<string, string> = {
  // Wallet errors
  "user rejected": "Transaction cancelled in wallet",
  "user denied": "Transaction cancelled in wallet",
  "rejected the request": "Transaction cancelled in wallet",

  // Insufficient funds
  "insufficient funds": "Insufficient SOL to pay transaction fees",
  "insufficient lamports": "Insufficient SOL to pay transaction fees",
  "0x1": "Insufficient SOL to pay transaction fees",

  // Network errors
  "failed to fetch": "Network error - please check your connection",
  "network request failed": "Network error - please check your connection",
  timeout: "Network is busy, please try again",
  "timed out": "Network is busy, please try again",

  // RPC errors
  "403": "RPC access denied - please refresh and try again",
  "429": "Too many requests - please wait a moment and try again",
  "503": "RPC service unavailable - please try again later",
  "blockhash not found": "Network congestion - please try again",

  // Transaction errors
  "block height exceeded": "Transaction expired - please try again",
  "blockhash expired": "Transaction expired - please try again",
  "already been processed": "This transaction was already processed",

  // Token-specific errors
  "owner does not match": "You do not own this token account",
  "insufficient token balance": "Insufficient token balance",
  "token account not found": "Token account not found",
  "invalid mint": "Invalid token mint address",

  // Program errors
  "custom program error": "Token program rejected the transaction",
  "transfer hook": "This token has restrictions and cannot be burned normally",
  frozen: "This token account is frozen",
};

/**
 * Convert a technical error to a user-friendly message
 * @param error - The error object or string
 * @returns User-friendly error message
 */
export function getUserFriendlyError(error: unknown): string {
  const errorString =
    error instanceof Error ? error.message.toLowerCase() : String(error).toLowerCase();

  // Check for known error patterns
  for (const [pattern, message] of Object.entries(ERROR_MESSAGES)) {
    if (errorString.includes(pattern.toLowerCase())) {
      return message;
    }
  }

  // If no pattern matched, return a generic message with the original
  if (error instanceof Error) {
    // Truncate very long error messages
    const msg = error.message;
    if (msg.length > 100) {
      return `Error: ${msg.slice(0, 100)}...`;
    }
    return `Error: ${msg}`;
  }

  return "An unexpected error occurred. Please try again.";
}

/**
 * Check if an error indicates the transaction might have succeeded
 * despite the error (e.g., timeout during confirmation)
 */
export function mightHaveSucceeded(error: unknown): boolean {
  const errorString =
    error instanceof Error ? error.message.toLowerCase() : String(error).toLowerCase();

  const ambiguousPatterns = [
    "timeout",
    "timed out",
    "block height exceeded",
    "blockhash expired",
    "confirmation",
  ];

  return ambiguousPatterns.some((pattern) => errorString.includes(pattern));
}
